import { useSearchParams } from 'next/navigation';

import { useEffect, useMemo } from 'react';

import { OneInchNetwork, OneInchToken } from '@api';

import {
  MAGIC_SWAP_DEFAULT_NETWORK_ID,
  MAGIC_SWAP_DEFAULT_TOKEN_ADDRESS_TO_RECEIVE,
  MAGIC_SWAP_DEFAULT_TOKEN_TO_SELL,
  ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS,
} from '@shared/constants/magicSwap';
import displayErrorToast from '@shared/helpers/displayErrorToast';

const useTokenPreselection = ({
  network: currentNetwork,
  networks,
  tokens,
  setNetwork,
  setTokenTo,
  setTokenFrom,
  isLoading,
}: {
  networks: OneInchNetwork[];
  tokens: OneInchToken[];
  network?: OneInchNetwork;
  setNetwork: (network: OneInchNetwork) => void;
  setTokenFrom: (token: OneInchToken) => void;
  setTokenTo: (token: OneInchToken) => void;
  tokenTo?: OneInchToken;
  tokenFrom?: OneInchToken;
  isLoading: boolean;
}) => {
  const searchParams = useSearchParams();
  const networkFromSearch = searchParams.get('network');
  const tokenFromSearch = searchParams.get('token');
  const preselectedNetworkId = networkFromSearch ?? MAGIC_SWAP_DEFAULT_NETWORK_ID;
  const preselectedTokenAddress = networkFromSearch
    ? tokenFromSearch
    : MAGIC_SWAP_DEFAULT_TOKEN_ADDRESS_TO_RECEIVE;

  const network = useMemo(
    () => networks.find((net) => net.id === Number(preselectedNetworkId)),
    [networks, preselectedNetworkId],
  );

  const getIsNativeToken = (token: OneInchToken) => ({
    isNativeToken: token.id.toLowerCase() === ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS.toLowerCase(),
  });

  const { nativeToken, tokenToSell, tokenToReceive } = useMemo(() => {
    let nativeTokenInner: OneInchToken | undefined;
    let tokenToSellInner: OneInchToken | undefined;
    let tokenToReceiveInner: OneInchToken | undefined;
    let index = tokens.length - 1;

    while ((!nativeTokenInner || !tokenToSellInner || !tokenToReceiveInner) && index >= 0) {
      const token = tokens[index];

      const { isNativeToken } = getIsNativeToken(token);
      const isTokenToSell =
        token.attributes.symbol.toLowerCase() === MAGIC_SWAP_DEFAULT_TOKEN_TO_SELL?.toLowerCase();
      const isTokenToReceive = token.id.toLowerCase() === preselectedTokenAddress?.toLowerCase();

      if (isNativeToken) {
        nativeTokenInner = token;
      }

      if (isTokenToSell) {
        tokenToSellInner = token;
      }

      if (isTokenToReceive) {
        tokenToReceiveInner = token;
      }

      --index;
    }

    // Case 1:
    //    If token to sell not found,
    // Case 2:
    //    If token to receive not found,
    //    then set native token as "token to sell"
    if (!tokenToSellInner || !tokenToReceiveInner) {
      tokenToSellInner = nativeTokenInner;
    }

    // Case 3:
    //    If "token to receive" and "token to sell" are the same,
    //    then set "token to sell" as empty if it is native token,
    //    otherwise set it as native token
    const isTokensSame =
      tokenToReceiveInner && tokenToSellInner && tokenToSellInner.id === tokenToReceiveInner.id;

    // Note: tokenToSellInner here just to calm TS down
    if (isTokensSame && tokenToSellInner) {
      const { isNativeToken } = getIsNativeToken(tokenToSellInner);

      if (isNativeToken) {
        tokenToSellInner = undefined;
      } else {
        tokenToSellInner = nativeTokenInner;
      }
    }

    return {
      nativeToken: nativeTokenInner,
      tokenToSell: tokenToSellInner,
      tokenToReceive: tokenToReceiveInner,
    };
  }, [tokens, preselectedTokenAddress]);

  useEffect(() => {
    try {
      if (!network || isLoading) {
        return;
      }

      if (!currentNetwork) {
        setNetwork(network);
      }

      if (tokenToSell) {
        setTokenFrom(tokenToSell);
      }

      if (tokenToReceive) {
        setTokenTo(tokenToReceive);
      }
    } catch (error) {
      displayErrorToast({ error });
    }
  }, [
    networks,
    tokens,
    isLoading,
    network,
    nativeToken,
    tokenToSell,
    tokenToReceive,
    currentNetwork,
    setNetwork,
    setTokenFrom,
    setTokenTo,
  ]);

  return {
    nativeToken,
    tokenToSell,
    tokenToReceive,
  };
};

export default useTokenPreselection;
